import { ChevronRight, StickyNote } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import type { SongListItemDTO } from '../../../shared/dto/library';
import { useT } from '../../i18n';
import { creditsLine } from './format';
import { KindIcons } from './KindIcons';
import { OfflineBadge } from './OfflineBadge';

/**
 * One tappable song row (≥ 64 px tall): title, composer/arranger, kind icons, offline badge.
 * `action` (e.g. the "Vista fyrir ónettengt" button) sits beside the link and replaces the badge,
 * since it shows the same offline state.
 */
export function SongRow({
  song,
  to,
  leading,
  note,
  showCounts,
  action,
}: {
  song: SongListItemDTO;
  to: string;
  leading?: ReactNode;
  note?: string | null;
  showCounts?: boolean;
  action?: ReactNode;
}) {
  const t = useT();
  const credits = creditsLine(t, song);
  return (
    <li className={action ? 'flex items-center' : undefined}>
      <Link
        to={to}
        className="flex min-h-16 min-w-0 flex-1 items-center gap-3 px-4 py-3 transition hover:bg-[var(--surface-2)] active:bg-[var(--accent-soft)] md:px-5"
      >
        {leading}
        <div className="min-w-0 flex-1">
          <div className="text-[18px] leading-snug font-semibold break-words">{song.title}</div>
          {credits ? <div className="truncate text-[15px] text-[var(--muted)]">{credits}</div> : null}
          {note ? (
            <div className="mt-1 flex items-start gap-1.5 text-[15px] text-[var(--ink-2)] italic">
              <StickyNote className="mt-0.5 size-4 shrink-0 text-[var(--muted)]" aria-hidden />
              <span className="break-words">{note}</span>
            </div>
          ) : null}
        </div>
        {action ? null : <OfflineBadge songId={song.id} />}
        <KindIcons counts={song.counts} showCounts={showCounts} />
        <ChevronRight className="size-5 shrink-0 text-[var(--muted)]" aria-hidden />
      </Link>
      {action}
    </li>
  );
}
